import { Suspense, useCallback, useMemo, useState } from "react";
import { DashboardSectionId, DashboardThread } from "../../types.js";
import { useDashboardProjects } from "../../hooks/useDashboardProjects";
import { getProjectsDisplayStatus } from "../../projects/mappers/projectsMappers";
import { PROJECTS_TAB_OPTIONS } from "../../projects/types/projects.api";
import { createProjectKickoffThreadRequest } from "../../messages/api/messagesApi";
import { mapDashboardThreadRecordToViewModel } from "../../messages/mappers/messagesMappers";
import { uploadAdminFile } from "../../../../services/viewerProjectService";
import { posthog } from '../../../../lib/posthog';
import * as Sentry from '@sentry/react';
import type {
  CreateProjectFileKind,
  CreateProjectFileProgress,
  CreateProjectModalState,
  CreateProjectModalSubmission,
} from "../modals/CreateProjectModal";
import {
  DashboardFilterChip,
  type FilterOption,
} from "../primitives/DashboardFilterChip";
import { DashboardButton } from "../primitives/DashboardButton";
import { DashboardSectionHeader } from "../primitives/DashboardSectionHeader";
import { DashboardTabStrip } from "../primitives/DashboardTabStrip";
import { lazyWithRetry } from "../../../../utils/lazyWithRetry";

const MONTH_OPTIONS: FilterOption[] = [
  { label: "All time", value: "all" },
  { label: "This month", value: "this_month" },
  { label: "Last 3 months", value: "last_3_months" },
  { label: "Last year", value: "last_year" },
];

const STATUS_OPTIONS: FilterOption[] = [
  { label: "All statuses", value: "all" },
  { label: "Active", value: "active" },
  { label: "Staged", value: "staged" },
  { label: "Completed", value: "completed" },
];

const CreateProjectModal = lazyWithRetry(() =>
  import("../modals/CreateProjectModal").then((module) => ({
    default: module.CreateProjectModal,
  })),
);

type ProjectAttachmentUploadInput = {
  file: File;
  kind: CreateProjectFileKind;
};

const INITIAL_MODAL_STATE: CreateProjectModalState = {
  phase: "idle",
  errorMessage: null,
  fileProgress: [],
};

function ProjectsStatusPill({
  status,
}: {
  status: "active" | "staged" | "completed";
}) {
  const statusStyles: Record<typeof status, string> = {
    active: "dashboard-status-active border-sky-500/20 bg-sky-500/10 text-sky-300",
    staged: "dashboard-status-staged border-black/10 dark:border-white/10 bg-slate-500/10 text-slate-700 dark:text-slate-300",
    completed: "dashboard-status-completed border-emerald-500/20 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300",
  };
  const statusLabels: Record<typeof status, string> = {
    active: "Active",
    staged: "Staged",
    completed: "Completed",
  };
  return (
    <span
      className={[
        "inline-flex h-9 items-center rounded-full border px-3 text-[8px] font-bold uppercase tracking-[0.16em] sm:text-[9px]",
        statusStyles[status],
      ].join(" ")}
      data-oid="v_3jv-l"
    >
      {" "}
      {statusLabels[status]}{" "}
    </span>
  );
}
function ProjectsMetaPill({ label, value }: { label: string; value: string }) {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full border border-black/10 dark:border-white/10 bg-slate-50/70 dark:bg-slate-900/70 px-3 py-1.5 text-[11px] text-slate-700 dark:text-slate-300 sm:text-xs"
      data-oid="n11-s4d"
    >
      {" "}
      <span
        className="font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500"
        data-oid="2e4f85k"
      >
        {" "}
        {label}{" "}
      </span>{" "}
      <span className="font-medium text-slate-900 dark:text-slate-100" data-oid="02h-jn:">
        {" "}
        {value}{" "}
      </span>{" "}
    </span>
  );
}
export function ProjectsSection({
  registerSection,
  canMutate,
  onOpenProject,
  onCreateThread,
}: {
  registerSection: (
    sectionId: DashboardSectionId,
  ) => (node: HTMLElement | null) => void;
  canMutate: boolean;
  onOpenProject: (projectId: string, projectName: string) => void;
  onCreateThread?: (thread: DashboardThread) => void;
}) {
  const {
    activeTab,
    createProject,
    createProjectErrorMessage,
    deleteProject,
    filteredProjects,
    isCreatingProject,
    isProjectsLoading,
    projectsErrorMessage,
    refreshWorkspace,
    retryProjectsLoad,
    searchQuery,
    setActiveTab,
    setSearchQuery,
    summary,
  } = useDashboardProjects();
  const [creationNotice, setCreationNotice] = useState<string | null>(null);
  const [isCreateProjectModalOpen, setCreateProjectModalOpen] = useState(false);
  const [modalState, setModalState] = useState<CreateProjectModalState>(
    INITIAL_MODAL_STATE,
  );
  const closeCreateProjectModal = useCallback(() => {
    setCreateProjectModalOpen(false);
    setModalState(INITIAL_MODAL_STATE);
  }, []);
  const [monthFilter, setMonthFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");

  const clientOptions = useMemo<FilterOption[]>(() => {
    const unique = Array.from(
      new Set(filteredProjects.map((p) => p.companyName)),
    );
    return [
      { label: "All clients", value: "all" },
      ...unique.map((name) => ({ label: name, value: name })),
    ];
  }, [filteredProjects]);

  const displayedProjects = useMemo(() => {
    if (clientFilter === "all") return filteredProjects;
    return filteredProjects.filter((p) => p.companyName === clientFilter);
  }, [filteredProjects, clientFilter]);
  async function uploadProjectAttachments(
    projectId: string,
    files: ProjectAttachmentUploadInput[],
  ) {
    const uploadedFiles: Array<{
      fileId: string;
      fileName: string;
      kind: ProjectAttachmentUploadInput["kind"];
    }> = [];
    const failedUploads: Array<{
      fileName: string;
      kind: ProjectAttachmentUploadInput["kind"];
      message: string;
    }> = [];

    posthog.capture('project_files_upload_batch_started', {
      project_id: projectId,
      file_count: files.length,
      files: files.map((f) => ({
        kind: f.kind,
        name: f.file.name,
        size_bytes: f.file.size,
        mime_type: f.file.type,
      })),
    });

    for (const file of files) {
      // Mark this file as uploading
      setModalState((prev) => ({
        ...prev,
        fileProgress: prev.fileProgress.map((entry) =>
          entry.kind === file.kind && entry.fileName === file.file.name
            ? { ...entry, status: "uploading", message: null }
            : entry,
        ),
      }));

      try {
        const uploaded = await uploadAdminFile({
          file: file.file,
          projectId,
        });
        uploadedFiles.push({
          fileId: uploaded.fileId,
          fileName: file.file.name,
          kind: file.kind,
        });
        setModalState((prev) => ({
          ...prev,
          fileProgress: prev.fileProgress.map((entry) =>
            entry.kind === file.kind && entry.fileName === file.file.name
              ? { ...entry, status: "success", message: null }
              : entry,
          ),
        }));
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Unable to finish staging this file.";
        failedUploads.push({
          fileName: file.file.name,
          kind: file.kind,
          message,
        });
        setModalState((prev) => ({
          ...prev,
          fileProgress: prev.fileProgress.map((entry) =>
            entry.kind === file.kind && entry.fileName === file.file.name
              ? { ...entry, status: "failed", message }
              : entry,
          ),
        }));
        Sentry.addBreadcrumb({
          category: 'project.upload',
          level: 'warning',
          message: 'Project attachment upload failed',
          data: {
            projectId,
            kind: file.kind,
            fileName: file.file.name,
            sizeBytes: file.file.size,
            mimeType: file.file.type,
            errorMessage: message,
          },
        });
        Sentry.captureException(error, {
          tags: {
            feature: 'project-create',
            stage: 'upload',
            kind: file.kind,
          },
          contexts: {
            upload: {
              projectId,
              fileName: file.file.name,
              sizeBytes: file.file.size,
              mimeType: file.file.type,
            },
          },
        });
      }
    }

    posthog.capture('project_files_upload_batch_completed', {
      project_id: projectId,
      total_count: files.length,
      uploaded_count: uploadedFiles.length,
      failed_count: failedUploads.length,
      failed_details: failedUploads.map((f) => ({
        kind: f.kind,
        name: f.fileName,
        message: f.message,
      })),
    });

    return {
      failedUploads,
      uploadedFiles,
    };
  }

  async function handleCreateProject(submission: CreateProjectModalSubmission) {
    console.info("CREATE_PROJECT_INPUT", {
      title: submission.title,
      startThread: submission.startThread,
      threadMessageLength: submission.threadMessage.length,
      selectedFiles: {
        cadFile: submission.cadFile?.name ?? null,
        modelFile: submission.modelFile?.name ?? null,
        pdfFile: submission.pdfFile?.name ?? null,
        videoFile: submission.videoFile?.name ?? null,
      },
    });

    const selectedFiles: ProjectAttachmentUploadInput[] = [
      submission.cadFile
        ? { kind: "cad" as const, file: submission.cadFile }
        : null,
      submission.modelFile
        ? { kind: "model" as const, file: submission.modelFile }
        : null,
      submission.pdfFile
        ? { kind: "pdf" as const, file: submission.pdfFile }
        : null,
      submission.videoFile
        ? { kind: "video" as const, file: submission.videoFile }
        : null,
    ].filter((value): value is ProjectAttachmentUploadInput => Boolean(value));

    // Pre-flight: client-side file size validation. Failures surface inside
    // the modal so the user does not lose context.
    const MAX_FILE_SIZES: Record<CreateProjectFileKind, number> = {
      model: 200 * 1024 * 1024,
      cad: 500 * 1024 * 1024,
      pdf: 50 * 1024 * 1024,
      video: 500 * 1024 * 1024,
    };

    setModalState({
      phase: "validating",
      errorMessage: null,
      fileProgress: selectedFiles.map<CreateProjectFileProgress>((file) => ({
        kind: file.kind,
        fileName: file.file.name,
        status: "pending",
        message: null,
      })),
    });

    for (const file of selectedFiles) {
      const maxSize = MAX_FILE_SIZES[file.kind] ?? 50 * 1024 * 1024;
      if (file.file.size > maxSize) {
        const message = `File "${file.file.name}" exceeds the ${Math.round(maxSize / (1024 * 1024))}MB limit for ${file.kind} files.`;
        setModalState((prev) => ({
          phase: "failed",
          errorMessage: message,
          fileProgress: prev.fileProgress.map((entry) =>
            entry.kind === file.kind && entry.fileName === file.file.name
              ? { ...entry, status: "failed", message }
              : entry,
          ),
        }));
        Sentry.addBreadcrumb({
          category: 'project.create',
          level: 'warning',
          message: 'Pre-flight file size check failed',
          data: {
            kind: file.kind,
            fileName: file.file.name,
            sizeBytes: file.file.size,
            maxBytes: maxSize,
          },
        });
        return;
      }
    }

    posthog.capture('project_creation_started', {
      title: submission.title,
      file_count: selectedFiles.length,
    });

    setModalState((prev) => ({
      ...prev,
      phase: "creating",
      errorMessage: null,
    }));

    const nextProject = await createProject({
      title: submission.title,
    });
    if (!nextProject) {
      console.error("CREATE_PROJECT_UI_ABORTED", {
        title: submission.title,
      });
      setModalState((prev) => ({
        ...prev,
        phase: "failed",
        errorMessage:
          createProjectErrorMessage ??
          "Unable to create this project right now.",
      }));
      posthog.capture('project_creation_failed', {
        title: submission.title,
        stage: 'creating',
        message: createProjectErrorMessage ?? null,
      });
      Sentry.captureMessage('Project create aborted', {
        level: 'error',
        tags: { feature: 'project-create', stage: 'creating' },
        contexts: {
          create: {
            title: submission.title,
            errorMessage: createProjectErrorMessage ?? null,
          },
        },
      });
      return;
    }

    const selectedFileCount = selectedFiles.length;

    if (selectedFileCount === 0) {
      // No uploads required — success immediately.
      setModalState((prev) => ({
        ...prev,
        phase: "success",
        errorMessage: null,
      }));

      posthog.capture('project_creation_succeeded', {
        project_id: nextProject.id,
        title: submission.title,
        file_count: 0,
      });
      posthog.capture('project_created', {
        project_id: nextProject.id,
        title: submission.title,
        file_count: 0,
      });

      const noticeParts = [
        `${nextProject.name} was created for ${nextProject.companyName}.`,
        "No files staged yet.",
      ];

      // Optional: kickoff thread when the user requested one.
      if (submission.startThread) {
        const kickoffResult = await createProjectKickoffThreadRequest({
          projectId: nextProject.id,
          openingMessage: submission.threadMessage,
          selectedFileCount: 0,
          uploadedFileCount: 0,
          failedUploadCount: 0,
        });
        if (kickoffResult.ok) {
          onCreateThread?.(
            mapDashboardThreadRecordToViewModel(kickoffResult.data.thread),
          );
          noticeParts.push(
            kickoffResult.data.created
              ? "A kickoff message thread is ready."
              : "The existing kickoff message thread is ready.",
          );
        } else {
          console.error("PROJECT_KICKOFF_THREAD_ERROR", {
            projectId: nextProject.id,
            projectTitle: nextProject.name,
            selectedFileCount: 0,
            uploadedFileCount: 0,
            failedUploadCount: 0,
            message: kickoffResult.error.message,
          });
          noticeParts.push(
            `Kickoff thread creation failed: ${kickoffResult.error.message}`,
          );
        }
      }

      setCreationNotice(noticeParts.join(" "));
      return;
    }

    setModalState((prev) => ({
      ...prev,
      phase: "uploading",
      errorMessage: null,
    }));
    posthog.capture('project_creation_uploading', {
      project_id: nextProject.id,
      file_count: selectedFileCount,
    });

    const { failedUploads, uploadedFiles } = await uploadProjectAttachments(
      nextProject.id,
      selectedFiles,
    );

    if (failedUploads.length > 0) {
      console.error("PROJECT_FILE_UPLOAD_ERROR", {
        projectId: nextProject.id,
        projectTitle: nextProject.name,
        selectedFileCount,
        failedUploads,
      });

      // Roll back the project so the DB doesn't keep an orphan.
      setModalState((prev) => ({
        ...prev,
        phase: "failed_rolling_back",
        errorMessage: `${failedUploads.length} file${failedUploads.length === 1 ? "" : "s"} failed to upload. Rolling back the project…`,
      }));

      posthog.capture('project_creation_failed', {
        project_id: nextProject.id,
        title: submission.title,
        stage: 'uploading',
        failed_count: failedUploads.length,
      });

      Sentry.addBreadcrumb({
        category: 'project.create',
        level: 'error',
        message: 'Project file uploads failed; rolling back',
        data: {
          projectId: nextProject.id,
          selectedFileCount,
          failedCount: failedUploads.length,
          failures: failedUploads.map((f) => ({
            fileName: f.fileName,
            kind: f.kind,
            message: f.message,
          })),
        },
      });

      const rollback = await deleteProject(nextProject.id);
      const firstFailure = failedUploads[0];
      const failureMessage = firstFailure
        ? `Upload failed: ${firstFailure.fileName} — ${firstFailure.message}`
        : "One or more files failed to upload.";
      const fullMessage = rollback.ok
        ? `${failureMessage} The project was not created.`
        : `${failureMessage} The project could not be fully rolled back. Please refresh and remove it manually if it persists.`;

      Sentry.captureMessage(
        rollback.ok
          ? 'Project rolled back after upload failure'
          : 'Project rollback failed after upload failure',
        {
          level: rollback.ok ? 'warning' : 'error',
          tags: {
            feature: 'project-create',
            stage: 'rollback',
            rollback_ok: String(rollback.ok),
          },
          contexts: {
            create: {
              projectId: nextProject.id,
              title: submission.title,
              failedCount: failedUploads.length,
              firstFailure: firstFailure ?? null,
              rollbackMessage: rollback.message ?? null,
            },
          },
        },
      );

      setModalState((prev) => ({
        ...prev,
        phase: "failed",
        errorMessage: fullMessage,
      }));

      posthog.capture('project_creation_rolled_back', {
        project_id: nextProject.id,
        title: submission.title,
        rollback_ok: rollback.ok,
        failed_count: failedUploads.length,
      });
      return;
    }

    // All uploads succeeded.
    posthog.capture('project_creation_succeeded', {
      project_id: nextProject.id,
      title: submission.title,
      file_count: uploadedFiles.length,
    });
    posthog.capture('project_created', {
      project_id: nextProject.id,
      title: submission.title,
      file_count: uploadedFiles.length,
    });

    console.info("PROJECT_CREATE_PERSISTED", {
      projectId: nextProject.id,
      projectTitle: nextProject.name,
      selectedFileCount,
      uploadedFileIds: uploadedFiles.map((file) => file.fileId),
    });

    const noticeParts = [
      `${nextProject.name} was created for ${nextProject.companyName}.`,
      uploadedFiles.length > 0
        ? `${uploadedFiles.length} file${uploadedFiles.length === 1 ? "" : "s"} staged in the persisted workspace file store.`
        : "No files staged yet.",
    ];

    if (submission.startThread) {
      const kickoffResult = await createProjectKickoffThreadRequest({
        projectId: nextProject.id,
        openingMessage: submission.threadMessage,
        selectedFileCount,
        uploadedFileCount: uploadedFiles.length,
        failedUploadCount: 0,
      });

      if (kickoffResult.ok) {
        onCreateThread?.(mapDashboardThreadRecordToViewModel(kickoffResult.data.thread));
        noticeParts.push(
          kickoffResult.data.created
            ? "A kickoff message thread is ready."
            : "The existing kickoff message thread is ready.",
        );
      } else {
        console.error("PROJECT_KICKOFF_THREAD_ERROR", {
          projectId: nextProject.id,
          projectTitle: nextProject.name,
          selectedFileCount,
          uploadedFileCount: uploadedFiles.length,
          failedUploadCount: 0,
          message: kickoffResult.error.message,
        });
        noticeParts.push(
          `Kickoff thread creation failed: ${kickoffResult.error.message}`,
        );
      }
    }

    setCreationNotice(noticeParts.join(" "));
    setModalState((prev) => ({
      ...prev,
      phase: "success",
      errorMessage: null,
    }));
  }

  return (
    <section
      id="projects"
      ref={registerSection("projects")}
      className="flex flex-col gap-6"
      data-oid="rsdpo27"
    >
      {" "}
      <DashboardSectionHeader
        eyebrow="Project Overview"
        title="Projects"
        meta={`${summary.total} total projects • ${summary.active} active • ${summary.staged} staged • ${summary.completed} completed`}
        data-oid="9:j7vdj"
      >
        <DashboardButton
          className="w-full whitespace-nowrap sm:w-auto sm:shrink-0"
          onClick={() => setCreateProjectModalOpen(true)}
          disabled={isCreatingProject}
          type="button"
          data-oid="tr-dx55"
        >
          <span
            aria-hidden="true"
            className="material-symbols-outlined text-[14px]"
            data-oid="k58vmin"
          >
            add
          </span>
          New Project
        </DashboardButton>
      </DashboardSectionHeader>{" "}
      {creationNotice ? (
        <div
          className="rounded-xl border border-sky-500/20 bg-sky-500/10 px-4 py-3 text-sm text-sky-800 dark:text-sky-100"
          data-oid="dz4.386"
        >
          {creationNotice}
        </div>
      ) : null}{" "}
      {isCreatingProject ? (
        <div
          className="rounded-xl border border-sky-500/20 bg-sky-500/10 px-4 py-2 text-xs text-sky-100"
          role="status"
          data-oid="projects-create-status"
        >
          Creating project...
        </div>
      ) : null}
      {createProjectErrorMessage ? (
        <div
          className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-200"
          role="status"
          data-oid="projects-create-error"
        >
          {createProjectErrorMessage}
        </div>
      ) : null}
      {projectsErrorMessage ? (
        <div
          className="dashboard-error-banner rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100"
          role="status"
          data-oid="projects-load-error"
        >
          <div
            className="flex flex-wrap items-center justify-between gap-3"
            data-oid="projects-load-error-row"
          >
            <div data-oid="projects-load-error-copy">
              <p className="font-semibold" data-oid="projects-load-error-title">
                {projectsErrorMessage}
              </p>
              <p
                className="mt-1 text-xs text-rose-200/80"
                data-oid="projects-load-error-body"
              >
                Retry the project list sync to restore backend results.
              </p>
            </div>
            <button
              className="retry-btn inline-flex items-center gap-2 rounded-lg border border-rose-300/40 bg-rose-100/10 px-3 py-1.5 text-xs font-semibold text-rose-100 transition-colors hover:bg-rose-100/20"
              onClick={retryProjectsLoad}
              type="button"
              data-oid="projects-load-retry"
            >
              <span
                className="material-symbols-outlined text-sm"
                data-oid="projects-load-retry-icon"
              >
                refresh
              </span>
              Retry
            </button>
          </div>
        </div>
      ) : null}
      {isProjectsLoading && displayedProjects.length > 0 ? (
        <div
          className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-xs text-slate-300"
          role="status"
          data-oid="projects-load-status"
        >
          Syncing projects list...
        </div>
      ) : null}
      <div
        className="flex items-center gap-3 overflow-x-auto pb-1 -mx-1 px-1 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0"
        data-oid="27wx1nm"
      >
        {" "}
        <DashboardFilterChip
          label="Month"
          value={monthFilter}
          options={MONTH_OPTIONS}
          onChange={setMonthFilter}
          data-oid="46011bz"
        />{" "}
        <DashboardFilterChip
          label="Client"
          value={clientFilter}
          options={clientOptions}
          onChange={setClientFilter}
          data-oid="l1npxi-"
        />{" "}
        <DashboardFilterChip
          label="Status"
          value={activeTab}
          options={STATUS_OPTIONS}
          onChange={(v) => setActiveTab(v as typeof activeTab)}
          data-oid="w_r0ji6"
        />{" "}
        <button
          className="dashboard-clear-btn px-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500 transition-colors hover:text-slate-700 dark:hover:text-slate-300"
          type="button"
          onClick={() => {
            setMonthFilter("all");
            setClientFilter("all");
            setActiveTab("all");
          }}
          data-oid="ufdw0u3"
        >
          {" "}
          Clear{" "}
        </button>{" "}
      </div>{" "}
      <div className="flex flex-col gap-6" data-oid="sypir11">
        {" "}
        <div className="relative w-full max-w-2xl" data-oid="j78ckxz">
          {" "}
          <span
            aria-hidden="true"
            className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-slate-500"
            data-oid="-gzyv14"
          >
            {" "}
            search{" "}
          </span>{" "}
          <input
            aria-label="Search projects"
            className="dashboard-content-search typography-input h-9 w-full rounded-lg border border-white/10 bg-slate-900/60 pl-9 pr-3 text-xs text-slate-200 outline-none transition-colors placeholder:text-slate-600 focus:border-sky-500/60 focus:ring-1 focus:ring-sky-500/40"
            placeholder="Search project or assets..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            type="text"
            data-oid="dik46pk"
          />{" "}
        </div>{" "}
        <DashboardTabStrip
          tabs={PROJECTS_TAB_OPTIONS}
          activeTab={activeTab}
          onSelect={setActiveTab}
          data-oid="dkn.xcu"
        />{" "}
      </div>{" "}
      <div className="flex flex-col gap-4" data-oid="u.ffqi7">
        {" "}
        {displayedProjects.length > 0 ? (
          displayedProjects.map((project) => (
            <div
              key={project.id}
              className="dashboard-project-card-item content-card flex flex-col gap-3 rounded-xl border border-white/10 bg-slate-900 p-3 transition-all sm:p-4 lg:flex-row lg:items-center lg:justify-between"
              data-oid="8xzlnsd"
            >
              {" "}
              <div className="min-w-0 flex-1" data-oid="yma2:qw">
                {" "}
                <h3
                  className="text-xs font-semibold text-white sm:text-[13px]"
                  data-oid="47r725c"
                >
                  {" "}
                  {project.name}{" "}
                </h3>{" "}
                <p
                  className="project-meta mt-0.5 text-[10px] text-slate-500 sm:text-[11px]"
                  data-oid="s7eoz1p"
                >
                  {" "}
                  {project.companyName} - {project.updatedAtLabel}{" "}
                </p>{" "}
              </div>{" "}
              <div
                className="flex w-full flex-wrap items-center gap-2.5 sm:w-auto sm:justify-end"
                data-oid="1x1r5j5"
              >
                {" "}
                <div className="flex -space-x-1" data-oid="pfwj1l4">
                  {" "}
                  {project.collaborators.map((collaborator) => (
                    <div
                      key={collaborator}
                      className="flex h-5.5 w-5.5 items-center justify-center rounded-full border border-slate-900 bg-slate-700 text-[9px] font-semibold text-slate-200"
                      data-oid="ulnscew"
                    >
                      {" "}
                      {collaborator}{" "}
                    </div>
                  ))}{" "}
                </div>{" "}
                <ProjectsStatusPill
                  status={getProjectsDisplayStatus(project.status)}
                  data-oid="1by:fwp"
                />{" "}
                <button
                  aria-label={`Open project actions for ${project.name}`}
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                  onClick={() => onOpenProject(project.id, project.name)}
                  type="button"
                  data-oid="ekyq_oc"
                >
                  {" "}
                  <span
                    aria-hidden="true"
                    className="material-symbols-outlined text-sm"
                    data-oid="-w5r9cs"
                  >
                    {" "}
                    more_horiz{" "}
                  </span>{" "}
                </button>{" "}
              </div>{" "}
            </div>
          ))
        ) : isProjectsLoading ? (
          <div
            className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-xs text-slate-300"
            role="status"
            data-oid="projects-empty-loading"
          >
            Syncing projects list...
          </div>
        ) : (
          <div
            className="dashboard-empty-state content-card rounded-xl border border-white/10 bg-slate-900 p-4"
            data-oid="zzp_ffc"
          >
            {" "}
            <h3 className="text-sm font-semibold text-white" data-oid="tdpi--d">
              {" "}
              No projects found{" "}
            </h3>{" "}
            <p className="mt-1 text-xs text-slate-500" data-oid="7l-cigr">
              {" "}
              Try adjusting your search or filters.{" "}
            </p>{" "}
          </div>
        )}{" "}
      </div>{" "}
      {isCreateProjectModalOpen ? (
        <Suspense fallback={null}>
          <CreateProjectModal
            onClose={closeCreateProjectModal}
            onCreate={handleCreateProject}
            state={modalState}
          />
        </Suspense>
      ) : null}{" "}
    </section>
  );
}
